import {
  CreateEmployeeResponseSchema,
  DeleteEmployeeResponseSchema,
  EmployeeDetailResponseSchema,
  ListEmployeesResponseSchema,
  UpdateEmployeeResponseSchema,
} from '@hrms/contracts/employees';
import type { NewEmployee, UpdateEmployee } from '@hrms/contracts/employees';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/** Employee row as returned by the list endpoint (unbranded, matches wire format). */
export type EmployeeRow = z.infer<typeof ListEmployeesResponseSchema>['items'][number];
/** Employee record as returned by the detail endpoint. */
export type EmployeeDetail = z.infer<typeof EmployeeDetailResponseSchema>['employee'];

interface EmployeeFilters {
  status?: string;
  search?: string;
  departmentId?: string;
  cursor?: string;
}

function buildEmployeeQuery(filters?: EmployeeFilters): string {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.departmentId) params.set('departmentId', filters.departmentId);
  if (filters?.cursor) params.set('cursor', filters.cursor);
  const qs = params.toString();
  return qs ? `/employees?${qs}` : '/employees';
}

export function useEmployees(filters?: EmployeeFilters) {
  return useQuery({
    queryKey: ['employees', filters ?? {}],
    queryFn: () => apiClient.get(buildEmployeeQuery(filters), ListEmployeesResponseSchema),
  });
}

export function useEmployee(id: string) {
  return useQuery({
    queryKey: ['employees', id],
    queryFn: () => apiClient.get(`/employees/${id}`, EmployeeDetailResponseSchema),
    enabled: id.length > 0,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NewEmployee) =>
      apiClient.post('/employees', data, CreateEmployeeResponseSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useUpdateEmployee(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateEmployee) =>
      apiClient.patch(`/employees/${id}`, data, UpdateEmployeeResponseSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/employees/${id}`, DeleteEmployeeResponseSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/** Returns id → displayName map for all employees (up to 200). */
export function useEmployeeMap(): Record<string, string> {
  const { data } = useEmployees();
  if (!data?.items) return {};
  return Object.fromEntries(data.items.map((e) => [e.id, e.displayName]));
}

/**
 * Pure mapping logic behind `useEmployeeMapByUserId`, extracted so it can be
 * unit-tested without React (this workspace has no DOM test environment).
 *
 * Employees with no linked user account (`userId === null`) are omitted —
 * they cannot own a job.
 */
export function buildEmployeeMapByUserId(
  items: readonly Pick<EmployeeRow, 'userId' | 'displayName'>[],
): Record<string, string> {
  return Object.fromEntries(
    items
      .filter((e): e is typeof e & { userId: string } => e.userId !== null)
      .map((e) => [e.userId, e.displayName]),
  );
}

/**
 * Employee display names keyed by USER id, not employee id.
 *
 * `agency_jobs.owner_id` references `users.id`, but `useEmployeeMap()` is keyed
 * by `employees.id` — two different id spaces. Anything resolving a job owner
 * must use this map, or it silently misses: the label falls back to a raw UUID
 * and, worse, an owner filter built from the wrong ids matches no rows at all.
 *
 * Employees with no linked user account are omitted — they cannot own a job.
 */
export function useEmployeeMapByUserId(): Record<string, string> {
  const { data } = useEmployees();
  if (!data?.items) return {};
  return buildEmployeeMapByUserId(data.items);
}
